import {
  flexRender,
  type CellContext,
  type ColumnDef,
  type FilterFn,
  type PaginationState,
  useTable,
} from "@tanstack/react-table";
import {
  type CSSProperties,
  type ChangeEvent,
  type FocusEvent,
  type PointerEvent,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  INITIAL_COLUMN_FILTERS,
  matchesNumericFilter,
  matchesPercentFilter,
  matchesScaledFilter,
  matchesSetFilter,
} from "./filters";
import {
  formatBound,
  formatKilometers,
  formatLatitude,
  formatLongitude,
  formatMeters,
  formatNumber,
  formatPercent,
  MISSING_VALUE,
} from "./formatters";
import {
  ColumnFilter,
  ColumnVisibilityPicker,
  columnMaximum,
  CountryCell,
  countryColumnMeta,
  footerStat,
  HiddenColumnNotices,
  interpolateColor,
  LatitudeCell,
  LongitudeCell,
  metricCell,
  renderColumnHeader,
  sequentialColor,
  textCell,
} from "./table-ui";
import {
  calculateFilteredAggregates,
  type FilteredAggregates,
} from "./table-core";
import { TABLE_FEATURES, type TableFeatures } from "./table-features";
import {
  isIntegerField,
  type SkiAreaDocument,
  type SkiAreaRecordSchema,
  type SkiAreaSummary,
} from "./types";
import { type TableUrlState, urlColumnsFrom, useUrlTableState, visibilityColumnsFrom } from "./url-state";

/** The view an untouched table shows, which a shared link leaves unwritten. */
const DEFAULT_STATE: TableUrlState = {
  columnFilters: [...INITIAL_COLUMN_FILTERS],
  sorting: [{ desc: true, id: "combined_vertical" }],
  columnVisibility: {
    country_code: false,
    country_subdiv_code: false,
    osm_status: false,
    ski_area_id: false,
    longitude: false,
    min_elevation: false,
  },
};

const KILOMETERS_PER_METER = 0.001;

const numericFilter: FilterFn<TableFeatures, SkiAreaSummary> = (
  row,
  columnId,
  value,
) =>
  matchesNumericFilter(row.getValue<number | null>(columnId), value);

const percentFilter: FilterFn<TableFeatures, SkiAreaSummary> = (
  row,
  columnId,
  value,
) =>
  matchesPercentFilter(row.getValue<number | null>(columnId), value);

/** Distances are stored in meters but bounded in kilometers. */
const kilometerFilter: FilterFn<TableFeatures, SkiAreaSummary> = (row, columnId, value) =>
  matchesScaledFilter(row.getValue<number | null>(columnId), value, KILOMETERS_PER_METER);

/** Keep rows whose value was selected in a column's value picker. */
const setFilter: FilterFn<TableFeatures, SkiAreaSummary> = (row, columnId, value) =>
  matchesSetFilter(row.getValue(columnId), value);

function AzimuthCell({
  getValue,
}: CellContext<TableFeatures, SkiAreaSummary, unknown>) {
  const azimuth = getValue<number | null>();
  if (azimuth === null) {
    return MISSING_VALUE;
  }
  return (
    <span className="oss-table-azimuth">
      <svg
        aria-hidden="true"
        className="oss-table-azimuth-arrow"
        style={{ transform: `rotate(${azimuth}deg)` }}
        viewBox="0 0 36 36"
      >
        <circle cx="18" cy="18" fill="currentColor" r="3" />
        <line stroke="currentColor" strokeWidth="3" x1="18" x2="18" y1="18" y2="6" />
        <polygon fill="currentColor" points="12,9 18,1.5 24,9" />
      </svg>
      <span>{formatNumber(azimuth)}°</span>
    </span>
  );
}

function DonutCell({
  getValue,
}: CellContext<TableFeatures, SkiAreaSummary, unknown>) {
  const value = getValue<number | null>();
  if (value === null) {
    return MISSING_VALUE;
  }
  const radius = 24;
  const circumference = 2 * Math.PI * radius;
  return (
    <span aria-label={formatPercent(value)} className="oss-table-donut">
      <svg aria-hidden="true" viewBox="0 0 60 60">
        <circle className="oss-table-donut-track" cx="30" cy="30" r={radius} />
        <circle
          className="oss-table-donut-value"
          cx="30"
          cy="30"
          r={radius}
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - value)}
        />
      </svg>
      <span>{formatPercent(value)}</span>
    </span>
  );
}

function roseTooltipPosition(element: HTMLElement) {
  const rect = element.getBoundingClientRect();
  const size = 300;
  const gap = 10;
  const left = Math.max(gap, rect.left - size - gap);
  const top = Math.max(
    gap,
    Math.min(
      Math.max(gap, rect.top + rect.height / 2 - size / 2),
      Math.max(gap, window.innerHeight - size - gap),
    ),
  );
  return { left, top };
}

function RoseCell({ row }: CellContext<TableFeatures, SkiAreaSummary, unknown>) {
  const [tooltip, setTooltip] = useState<{ left: number; top: number } | null>(null);
  // Rose SVGs are generated by `openskistats visualize` and are often absent
  // from a local preview build, so degrade to a muted placeholder rather than
  // a broken-image icon.
  const [unavailable, setUnavailable] = useState(false);
  const id = row.original.ski_area_id;
  const previewUrl = `/ski-areas/roses-preview/${id}.svg`;
  const fullUrl = `/ski-areas/roses-full/${id}.svg`;
  const showTooltip = (
    event: PointerEvent<HTMLAnchorElement> | FocusEvent<HTMLAnchorElement>,
  ) => setTooltip(roseTooltipPosition(event.currentTarget));

  if (unavailable) {
    return <span className="oss-table-rose-missing">{MISSING_VALUE}</span>;
  }

  return (
    <span className="oss-table-rose">
      <a
        aria-label={`Open full orientation rose for ${row.original.ski_area_name}`}
        href={fullUrl}
        onBlur={() => setTooltip(null)}
        onFocus={showTooltip}
        onPointerEnter={showTooltip}
        onPointerLeave={() => setTooltip(null)}
        rel="noreferrer"
        target="_blank"
      >
        <img
          alt={`Orientation rose preview for ${row.original.ski_area_name}`}
          loading="lazy"
          onError={() => setUnavailable(true)}
          src={previewUrl}
        />
      </a>
      {tooltip && (
        <span
          className="oss-table-rose-tooltip"
          style={{ left: tooltip.left, top: tooltip.top }}
        >
          <img alt="" src={fullUrl} />
        </span>
      )}
    </span>
  );
}

function divergingColor(value: number) {
  return value <= 0
    ? interpolateColor([232, 146, 0], [255, 255, 255], value + 1)
    : interpolateColor([255, 255, 255], [0, 125, 191], value);
}

function percentCell() {
  return ({ getValue }: CellContext<TableFeatures, SkiAreaSummary, unknown>) => {
    const value = getValue<number | null>();
    return <span>{formatPercent(value)}</span>;
  };
}

function aggregatesFrom(context: {
  table: { options: { meta?: { aggregates: unknown } } };
}): FilteredAggregates | undefined {
  return context.table.options.meta?.aggregates as FilteredAggregates | undefined;
}

function createColumns(
  data: readonly SkiAreaSummary[],
  schema: SkiAreaRecordSchema,
): ColumnDef<TableFeatures, SkiAreaSummary, unknown>[] {
  const fieldMaximum = (field: keyof SkiAreaSummary) => columnMaximum(data, field);
  const numericColumn = (
    field: keyof SkiAreaSummary,
    label: string,
    options: Partial<ColumnDef<TableFeatures, SkiAreaSummary, unknown>> = {},
  ): ColumnDef<TableFeatures, SkiAreaSummary, unknown> => ({
    accessorKey: field,
    filterFn: numericFilter,
    header: label,
    minSize: 36,
    sortUndefined: "last",
    ...options,
    id: field,
    meta: {
      filterVariant: "range",
      integer: isIntegerField(schema, field),
      ...options.meta,
    },
  });
  const percentColumn = (
    field: keyof SkiAreaSummary,
    label: string,
    options: Partial<ColumnDef<TableFeatures, SkiAreaSummary, unknown>> = {},
  ): ColumnDef<TableFeatures, SkiAreaSummary, unknown> => ({
    accessorKey: field,
    cell: percentCell(),
    filterFn: percentFilter,
    header: label,
    minSize: 36,
    sortUndefined: "last",
    ...options,
    id: field,
    meta: {
      cellStyle: (value) =>
        typeof value === "number"
          ? { backgroundColor: sequentialColor(value) }
          : {},
      // Stored as a fraction but filtered in whole percent, matching the cells.
      filterFormat: (value: number) => `${formatBound(value)}%`,
      filterScale: 100,
      filterVariant: "range",
      ...options.meta,
    },
  });

  return [
    {
      header: "",
      id: "ski-area-group",
      meta: { className: "oss-table-sticky" },
      columns: [
        {
          accessorKey: "ski_area_id",
          enableColumnFilter: false,
          id: "ski_area_id",
        },
        {
          accessorKey: "ski_area_name",
          enableHiding: false,
          cell: ({ getValue, row }) => (
            <a
              href={`https://openskimap.org/?obj=${row.original.ski_area_id}`}
              rel="noreferrer"
              target="_blank"
            >
              {getValue<string>()}
            </a>
          ),
          footer: (context) =>
            footerStat(
              "Distinct",
              formatNumber(aggregatesFrom(context)?.distinctCounts.ski_area_name ?? null),
            ),
          filterFn: setFilter,
          header: "Ski Area",
          id: "ski_area_name",
          meta: { facetSort: "label", filterVariant: "faceted" },
          minSize: 140,
          size: 160,
          sortDescFirst: false,
        },
        {
          accessorKey: "osm_status",
          id: "osm_status",
        },
      ],
    },
    {
      header: "Location",
      meta: { className: "oss-table-border-left" },
      columns: [
        {
          accessorKey: "country",
          cell: CountryCell,
          filterFn: setFilter,
          footer: (context) =>
            footerStat(
              "Distinct",
              formatNumber(aggregatesFrom(context)?.distinctCounts.country ?? null),
            ),
          header: "Country",
          id: "country",
          meta: {
            className: "oss-table-border-left",
            filterVariant: "faceted",
            ...countryColumnMeta(data),
          },
          minSize: 70,
          size: 85,
          sortDescFirst: false,
          sortUndefined: "last",
        },
        {
          accessorKey: "country_code",
          id: "country_code",
        },
        {
          accessorKey: "country_subdiv_code",
          cell: ({ getValue }) => textCell(getValue<string | null>()),
          footer: (context) =>
            footerStat(
              "Distinct",
              formatNumber(aggregatesFrom(context)?.distinctCounts.country_subdiv_code ?? null),
            ),
          filterFn: setFilter,
          header: "Subdivision",
          id: "country_subdiv_code",
          meta: { filterVariant: "faceted" },
          minSize: 65,
          size: 85,
          sortDescFirst: false,
          sortUndefined: "last",
        },
        {
          accessorKey: "region",
          cell: ({ getValue }) => textCell(getValue<string | null>()),
          footer: (context) =>
            footerStat(
              "Distinct",
              formatNumber(aggregatesFrom(context)?.distinctCounts.region ?? null),
            ),
          filterFn: setFilter,
          header: "Region",
          id: "region",
          meta: { filterVariant: "faceted" },
          minSize: 65,
          size: 85,
          sortDescFirst: false,
          sortUndefined: "last",
        },
        {
          accessorKey: "locality",
          cell: ({ getValue }) => textCell(getValue<string | null>()),
          footer: (context) =>
            footerStat(
              "Distinct",
              formatNumber(aggregatesFrom(context)?.distinctCounts.locality ?? null),
            ),
          filterFn: setFilter,
          header: "Locality",
          id: "locality",
          meta: { filterVariant: "faceted" },
          minSize: 65,
          size: 85,
          sortDescFirst: false,
          sortUndefined: "last",
        },
        {
          accessorKey: "latitude",
          cell: LatitudeCell,
          filterFn: numericFilter,
          header: "ℍ φ",
          id: "latitude",
          meta: { filterFormat: formatLatitude, filterVariant: "range" },
          minSize: 55,
          size: 62,
          sortUndefined: "last",
        },
        numericColumn("longitude", "ℍ λ", {
          cell: LongitudeCell,
          meta: { filterFormat: formatLongitude },
          minSize: 55,
          size: 62,
        }),
      ],
    },
    {
      header: "Downhill Runs",
      meta: { className: "oss-table-border-left" },
      columns: [
        numericColumn("run_count", "Runs", {
          cell: metricCell(fieldMaximum("run_count")),
          footer: (context) =>
            footerStat(
              "Sum",
              formatNumber(aggregatesFrom(context)?.sums.run_count ?? null),
            ),
          meta: { className: "oss-table-border-left" },
          minSize: 44,
          size: 52,
        }),
        numericColumn("lift_count", "Lifts", {
          cell: metricCell(fieldMaximum("lift_count")),
          footer: (context) =>
            footerStat(
              "Sum",
              formatNumber(aggregatesFrom(context)?.sums.lift_count ?? null),
            ),
          minSize: 44,
          size: 52,
        }),
        numericColumn("combined_vertical", "Vertical", {
          cell: metricCell(fieldMaximum("combined_vertical"), formatMeters),
          footer: (context) =>
            footerStat(
              "Sum",
              formatMeters(aggregatesFrom(context)?.sums.combined_vertical ?? null),
            ),
          minSize: 70,
          size: 80,
        }),
        numericColumn("combined_distance", "Distance", {
          cell: metricCell(fieldMaximum("combined_distance"), formatKilometers),
          filterFn: kilometerFilter,
          footer: (context) =>
            footerStat(
              "Sum",
              formatKilometers(aggregatesFrom(context)?.sums.combined_distance ?? null),
            ),
          meta: {
            filterFormat: (value: number) => `${formatBound(value, 1)} km`,
            filterScale: KILOMETERS_PER_METER,
          },
          minSize: 60,
          size: 72,
        }),
        numericColumn("min_elevation", "Base Elev", {
          cell: metricCell(fieldMaximum("min_elevation"), formatMeters),
          footer: (context) =>
            footerStat(
              "Min",
              formatMeters(aggregatesFrom(context)?.minimumElevation ?? null),
            ),
          minSize: 60,
          size: 72,
        }),
        numericColumn("max_elevation", "Peak Elev", {
          cell: metricCell(fieldMaximum("max_elevation"), formatMeters),
          footer: (context) =>
            footerStat(
              "Max",
              formatMeters(aggregatesFrom(context)?.maximumElevation ?? null),
            ),
          minSize: 60,
          size: 72,
        }),
        numericColumn("vertical_drop", "Drop", {
          cell: metricCell(fieldMaximum("vertical_drop"), formatMeters),
          footer: (context) =>
            footerStat(
              "Sum",
              formatMeters(aggregatesFrom(context)?.sums.vertical_drop ?? null),
            ),
          minSize: 60,
          size: 72,
        }),
        numericColumn("solar_irradiation_season", "Sunlight", {
          cell: metricCell(fieldMaximum("solar_irradiation_season"), (value) =>
            formatNumber(value, 1),
          ),
          minSize: 58,
          size: 70,
        }),
      ],
    },
    {
      header: "Mean Orientation",
      meta: { className: "oss-table-border-left" },
      columns: [
        numericColumn("bearing_mean", "Azimuth", {
          cell: AzimuthCell,
          meta: { className: "oss-table-border-left" },
          minSize: 52,
          size: 62,
        }),
        percentColumn("bearing_alignment", "Alignment", {
          cell: DonutCell,
          footer: (context) =>
            footerStat(
              "Wtd. Mean",
              formatPercent(aggregatesFrom(context)?.weightedMeans.bearing_alignment ?? null),
            ),
          meta: { cellStyle: () => ({}) },
          minSize: 64,
          size: 74,
        }),
        percentColumn("poleward_affinity", "Poleward", {
          cell: percentCell(),
          footer: (context) =>
            footerStat(
              "Wtd. Mean",
              formatPercent(aggregatesFrom(context)?.weightedMeans.poleward_affinity ?? null),
            ),
          meta: {
            cellStyle: (value) =>
              typeof value === "number"
                ? { backgroundColor: divergingColor(value) }
                : {},
          },
          minSize: 58,
          size: 68,
        }),
        percentColumn("eastward_affinity", "Eastward", {
          cell: percentCell(),
          footer: (context) =>
            footerStat(
              "Wtd. Mean",
              formatPercent(aggregatesFrom(context)?.weightedMeans.eastward_affinity ?? null),
            ),
          meta: {
            cellStyle: (value) =>
              typeof value === "number"
                ? { backgroundColor: divergingColor(value) }
                : {},
          },
          minSize: 58,
          size: 68,
        }),
      ],
    },
    {
      header: "Cardinal Directions",
      meta: { className: "oss-table-border-left" },
      columns: [
        percentColumn("run_proportion_4_north", "N₄", {
          footer: (context) =>
            footerStat(
              "Wtd. Mean",
              formatPercent(aggregatesFrom(context)?.weightedMeans.run_proportion_4_north ?? null),
            ),
          meta: { className: "oss-table-border-left" },
          minSize: 42,
          size: 50,
        }),
        percentColumn("run_proportion_4_east", "E₄", {
          footer: (context) =>
            footerStat(
              "Wtd. Mean",
              formatPercent(aggregatesFrom(context)?.weightedMeans.run_proportion_4_east ?? null),
            ),
          minSize: 42,
          size: 50,
        }),
        percentColumn("run_proportion_4_south", "S₄", {
          footer: (context) =>
            footerStat(
              "Wtd. Mean",
              formatPercent(aggregatesFrom(context)?.weightedMeans.run_proportion_4_south ?? null),
            ),
          minSize: 42,
          size: 50,
        }),
        percentColumn("run_proportion_4_west", "W₄", {
          footer: (context) =>
            footerStat(
              "Wtd. Mean",
              formatPercent(aggregatesFrom(context)?.weightedMeans.run_proportion_4_west ?? null),
            ),
          minSize: 42,
          size: 50,
        }),
      ],
    },
    {
      header: "",
      id: "north-south-group",
      meta: { className: "oss-table-border-left" },
      columns: [
        percentColumn("run_proportion_2_north", "N₂", {
          footer: (context) =>
            footerStat(
              "Wtd. Mean",
              formatPercent(aggregatesFrom(context)?.weightedMeans.run_proportion_2_north ?? null),
            ),
          meta: { className: "oss-table-border-left" },
          minSize: 42,
          size: 50,
        }),
      ],
    },
    {
      header: "",
      id: "rose-group",
      meta: { className: "oss-table-border-left" },
      columns: [
        {
          cell: RoseCell,
          enableColumnFilter: false,
          enableSorting: false,
          header: "Rose",
          id: "rose",
          meta: {
            className: "oss-table-border-left",
            description: "Preview of the ski area's run-orientation rose. Hover or focus for the full rose, or activate the link to open it.",
          },
          minSize: 54,
          size: 62,
        },
      ],
    },
  ];
}

export function SkiAreaTable({ document }: { document: SkiAreaDocument }) {
  const columns = useMemo(
    () => createColumns(document.ski_areas, document.record_schema),
    [document],
  );
  const urlSpec = useMemo(
    () => ({ columns: urlColumnsFrom(columns), visibilityColumns: visibilityColumnsFrom(columns), defaults: DEFAULT_STATE }),
    [columns],
  );
  const { columnFilters, columnVisibility, setColumnFilters, setColumnVisibility, setSorting, sorting } =
    useUrlTableState(urlSpec);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [aggregates, setAggregates] = useState(() =>
    calculateFilteredAggregates(document.ski_areas),
  );

  const table = useTable({
    features: TABLE_FEATURES,
    columns,
    data: document.ski_areas,
    defaultColumn: {
      maxSize: 190,
      minSize: 36,
      size: 55,
    },
    initialState: {
      columnVisibility: DEFAULT_STATE.columnVisibility,
    },
    meta: { aggregates },
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    state: { columnFilters, columnVisibility, pagination, sorting },
  });

  const filteredRows = table.getFilteredRowModel().rows;
  useEffect(() => {
    setAggregates(calculateFilteredAggregates(filteredRows.map((row) => row.original)));
  }, [filteredRows]);

  const pageCount = table.getPageCount();
  const hasFilters = columnFilters.length > 0;

  return (
    <div className="oss-table">
      <div className="oss-table-status" role="status">
        <span>
          Showing {formatNumber(filteredRows.length)} of {formatNumber(document.record_count)} named ski areas.
        </span>
        <div className="oss-table-actions">
        <ColumnVisibilityPicker table={table} />
        <button
          className="oss-table-button"
          disabled={!hasFilters}
          onClick={() => setColumnFilters([])}
          type="button"
        >
          Clear all filters
        </button>
        </div>
      </div>
      <HiddenColumnNotices table={table} />
      <div className="oss-table-scroll" tabIndex={0}>
        <table style={{ minWidth: table.getTotalSize(), width: "100%" }}>
          <colgroup>
            {table.getVisibleLeafColumns().map((column) => (
              <col key={column.id} style={{ width: column.getSize() }} />
            ))}
          </colgroup>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((headerCell) => (
                  <th
                    className={`${headerCell.column.columnDef.meta?.className ?? ""} ${headerCell.column.id === "ski_area_name" ? "oss-table-sticky" : ""}`}
                    colSpan={headerCell.colSpan}
                    key={headerCell.id}
                    scope={headerCell.colSpan > 1 ? "colgroup" : "col"}
                    style={{ width: headerCell.getSize() }}
                  >
                    {headerCell.isPlaceholder
                      ? null
                      : renderColumnHeader(
                          headerCell.getContext(),
                          document.record_schema.properties[headerCell.column.id]?.description,
                        )}
                    {!headerCell.isPlaceholder &&
                      headerCell.colSpan === 1 &&
                      headerCell.column.getCanFilter() && (
                        <ColumnFilter column={headerCell.column} />
                      )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td className="oss-table-empty" colSpan={table.getVisibleLeafColumns().length}>
                  No ski areas match the current filters. Clear or adjust a filter to continue.
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      className={`${cell.column.columnDef.meta?.className ?? ""} ${cell.column.id === "ski_area_name" ? "oss-table-sticky" : ""}`}
                      key={cell.id}
                      style={cell.column.columnDef.meta?.cellStyle?.(cell.getValue())}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
          <tfoot>
            {table.getFooterGroups().slice(0, 1).map((footerGroup) => (
              <tr key={footerGroup.id}>
                {footerGroup.headers.map((footerCell) => (
                  <td
                    className={`${footerCell.column.columnDef.meta?.className ?? ""} ${footerCell.column.id === "ski_area_name" ? "oss-table-sticky" : ""}`}
                    colSpan={footerCell.colSpan}
                    key={footerCell.id}
                  >
                    {footerCell.isPlaceholder
                      ? null
                      : flexRender(
                          footerCell.column.columnDef.footer,
                          footerCell.getContext(),
                        )}
                  </td>
                ))}
              </tr>
            ))}
          </tfoot>
        </table>
      </div>
      <div className="oss-table-pagination">
        <div className="oss-table-page-buttons">
          <button
            aria-label="First page"
            disabled={!table.getCanPreviousPage()}
            onClick={() => table.firstPage()}
            type="button"
          >
            «
          </button>
          <button
            aria-label="Previous page"
            disabled={!table.getCanPreviousPage()}
            onClick={() => table.previousPage()}
            type="button"
          >
            ‹
          </button>
          <span>
            Page {formatNumber(pageCount === 0 ? 0 : pagination.pageIndex + 1)} of {formatNumber(pageCount)}
          </span>
          <button
            aria-label="Next page"
            disabled={!table.getCanNextPage()}
            onClick={() => table.nextPage()}
            type="button"
          >
            ›
          </button>
          <button
            aria-label="Last page"
            disabled={!table.getCanNextPage()}
            onClick={() => table.lastPage()}
            type="button"
          >
            »
          </button>
        </div>
        <label>
          Rows per page
          <select
            onChange={(event) => table.setPageSize(Number(event.target.value))}
            value={pagination.pageSize}
          >
            {[10, 25, 50, 100].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>
        </label>
      </div>
    </div>
  );
}
